import { createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { AppState } from 'shared/store/reducers';
import { PeerStatus } from 'types/realtime';
import { selectActiveVisitor } from 'shared/store/shared-selectors';
import { makeLog } from 'shared/utils/log';
import { useAppSelector, store } from 'shared/store';
import {
  selectIsActiveVisitorOnline,
  RealtimeState,
  selectRealtimeAssociatedVisitor,
} from 'shared/store/realtime-slice';
import { startPeer, stopPeer } from './manager';
import { createKit } from './kit';

const log = makeLog('peer-kit');

type PA<T> = PayloadAction<T>;

/**
 *
 * Peer flow
 * - When associated, attempt to connect peer
 * - When dissociated, attempt to disconnect peer
 *
 */

/****************************************************
 *
 *                 Thunks
 *
 ****************************************************/

export const peerConnectActiveVisitor = createAsyncThunk<void, void, { state: AppState }>(
  'realtime/peerConnectActiveVisitor',
  async (_, { getState }) => {
    const activeVisitor = selectActiveVisitor(getState());
    if (activeVisitor) {
      await startPeer(getState);
    }
  },
);

export const peerDisconnectActiveVisitor = createAsyncThunk(
  'realtime/peerDisconnectActiveVisitor',
  async () => {
    await stopPeer();
  },
);

/****************************************************
 *
 *                 Reducers
 *
 ****************************************************/
const reducers = {
  peerStatusUpdated: (state: RealtimeState, action: PA<PeerStatus>) => {
    state.peerStatus = action.payload;
    state.isConnectedToPeer = action.payload === 'connected';
    state.isConnectingToPeer = action.payload === 'connecting';
    if (action.payload === 'connected') {
      state.peerConnectionError = '';
    }
    // On disconnect, reset all ephemeral peer state
    if (action.payload === 'disconnected') {
      state.incomingCameraStreamId = null;
      state.incomingDisplayStreamId = null;
      state.isReceivingAudio = false;
      state.isReceivingVideo = false;
    }
  },
};
// Access actions before createSlice so we can reference them locally
// const actions = createRealtimeReducerActions({ reducers });

export const peerKit = createKit({
  reducers,

  /****************************************************
   *
   *               Handle triggers
   *
   ****************************************************/
  triggerThunkHelper: () => null,

  /****************************************************
   *
   *         Visitor sync/update reducer helpers
   *
   ****************************************************/
  visitorUpdateReducer: () => null,

  /****************************************************
   *
   *               Kit hook
   *
   ****************************************************/
  useKitHook: () => {
    const dispatch = useDispatch();
    const { peerStatus, associationStatus, isConnectedToServer } = useAppSelector(
      s => s.realtime,
    );
    const isVisitorOnline = useAppSelector(selectIsActiveVisitorOnline);
    const disconnectDissociatedTimeout = useRef<null | number>(null);

    useEffect(
      function connectPeerWhenAssociated() {
        if (
          isConnectedToServer &&
          peerStatus === 'disconnected' &&
          associationStatus === 'active' &&
          isVisitorOnline
        ) {
          log.debug(`Fully associated with visitor, will start peer connection`);
          dispatch(peerConnectActiveVisitor());
        }
      },
      [isConnectedToServer, peerStatus, associationStatus, isVisitorOnline, dispatch],
    );

    useEffect(
      function disconnectPeerWhenDissociated() {
        const associated = associationStatus === 'active';
        const peerConnected = peerStatus === 'connected';
        if (!associated && peerConnected && !disconnectDissociatedTimeout.current) {
          log.debug(
            'Dissociated and peer connected, will disconnect in a few moments if appropriate...',
          );
          disconnectDissociatedTimeout.current = window.setTimeout(() => {
            disconnectDissociatedTimeout.current = null;
            const associatedVisitor = selectRealtimeAssociatedVisitor(store.getState());
            if (!associatedVisitor) {
              log.debug('...Dissociated and peer still connected, will disconnect peer');
              dispatch(peerDisconnectActiveVisitor());
            } else {
              log.debug(
                '...No need to disconnect peer, websocket likely reconnected and reassociated',
              );
            }
          }, 1000);
        }
      },
      [dispatch, peerStatus, associationStatus],
    );

    useEffect(
      function disconnectPeerWhenVisitorOffline() {
        if (!isVisitorOnline && peerStatus !== 'disconnected') {
          log.debug(
            `Visitor is offline (no sid) but peer is "${peerStatus}", will try to disconnect peer`,
          );
          dispatch(peerDisconnectActiveVisitor());
        }
      },
      [dispatch, isVisitorOnline, peerStatus],
    );
  },
});
